import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const YourBooth = () => {
    const [booths, setBooths] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const { appId, language } = useAppId();
    const navigate = useNavigate();

    // Safely get user_id from local storage
    const localUserId = localStorage.getItem('userId') || "1";
    const lang = language || 'en';

    // Translation dictionary
    const t = {
        en: {
            title: "Your Booths",
            loadingData: "Loading Booths...",
            searchPlaceholder: "Search by Booth Number...",
            boothNo: "Booth No.",
            totalVoters: "Total Voters",
            noResults: "No booths found matching",
            noData: "No booths available."
        },
        hi: {
            title: "आपके बूथ",
            loadingData: "बूथ लोड हो रहे हैं...",
            searchPlaceholder: "बूथ नंबर द्वारा खोजें...",
            boothNo: "बूथ नं.",
            totalVoters: "कुल मतदाता",
            noResults: "इससे मेल खाने वाला कोई बूथ नहीं मिला",
            noData: "कोई बूथ उपलब्ध नहीं है।"
        },
        gu: {
            title: "તમારા બૂથ",
            loadingData: "બૂથ લોડ થઈ રહ્યા છે...",
            searchPlaceholder: "બૂથ નંબર દ્વારા શોધો...",
            boothNo: "બૂથ નં.",
            totalVoters: "કુલ મતદારો",
            noResults: "કોઈ બૂથ મળ્યું નથી",
            noData: "કોઈ બૂથ ઉપલબ્ધ નથી."
        }
    };

    const txt = t[lang] || t.en;

    useEffect(() => {
        if (appId && localUserId) {
            fetchBooths();
        } else {
            navigate("/");
        }
    }, [appId, localUserId, navigate]);

    const fetchBooths = async () => {
        setLoading(true);
        try {
            const payload = {
                app_id: String(appId),
                user_id: String(localUserId)
            };

            const response = await axios.post(`${BASE_URL}/api/dis_booth_pramukh_by_sakti_pramukh`, payload);

            if (response.data.success && response.data.data && response.data.data.result) {
                setBooths(response.data.data.result);
            } else {
                setBooths([]);
            }
        } catch (err) {
            console.error("API Error:", err);
            setBooths([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredBooths = booths.filter(booth => {
        if (!searchTerm) return true;
        return booth.booth_no?.toString().includes(searchTerm);
    });

    if (loading) {
        return (
            <div className="app-container p-4">
                <Header title={txt.title} />
                <div className="loader-wrapper">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
                <style jsx>{`
                    .app-container { background: #f8fafc; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
                    .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                    .spinner { width: 50px; height: 50px; border: 4px solid #E5E7EB; border-top: 4px solid #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); }
                    .loader-text { color: #64748b; font-weight: 600; font-size: 1.1rem; animation: pulse 1.5s ease-in-out infinite; }
                    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                    @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
                `}</style>
            </div>
        );
    }

    return (
        <div className="app-container p-4">
            <Header title={txt.title} />

            {/* Professional Search Bar */}
            <div className="filter-container mt-4 mb-4">
                <div className="search-wrapper">
                    <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                    <input
                        type="text"
                        className="search-input"
                        placeholder={txt.searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        </button>
                    )}
                </div>
            </div>

            {/* Booth Grid */}
            <div className="booth-grid">
                {filteredBooths.length > 0 ? (
                    filteredBooths.map((booth, index) => (
                        <div 
                            key={index} 
                            className="booth-card"
                            onClick={() => navigate('/shakti_kendra_booth_wise_voter', { state: { selectedBoothNo: booth.booth_no } })}
                        >
                            <div className="card-top-accent"></div>
                            
                            <h5 className="booth-label">{txt.boothNo}</h5>
                            
                            <div className="booth-number-circle">
                                {booth.booth_no}
                            </div>

                            <div className="voter-stats-container">
                                <div className="voter-icon-wrapper">
                                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                        <circle cx="9" cy="7" r="4"></circle>
                                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                    </svg>
                                </div>
                                <div className="voter-info">
                                    <span className="voter-title">{txt.totalVoters}</span>
                                    <span className="voter-count">{booth.total_voter || 0}</span>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="no-data-message">
                        <div className="no-data-icon">🏢</div>
                        <div className="no-data-text">
                            {searchTerm ? `${txt.noResults} "${searchTerm}"` : txt.noData}
                        </div>
                    </div>
                )}
            </div>

            <style jsx>{`
                .app-container {
                    background: #E5E7EB;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                }

                /* --- SEARCH BAR STYLES --- */
                .filter-container {
                    display: flex;
                    width: 100%;
                }

                .search-wrapper {
                    position: relative;
                    width: 100%;
                    max-width: 500px;
                }

                .search-icon {
                    position: absolute;
                    left: 16px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                    width: 20px;
                    height: 20px;
                }

                .search-input {
                    width: 100%;
                    padding: 14px 44px 14px 48px;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    font-size: 15px;
                    color: #1e293b;
                    background-color: #ffffff;
                    outline: none;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                }

                .search-input:focus {
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
                }

                .clear-search-btn {
                    position: absolute;
                    right: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: #f1f5f9;
                    border: none;
                    color: #64748b;
                    padding: 6px;
                    border-radius: 50%;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                }

                .clear-search-btn:hover {
                    background: #e2e8f0;
                    color: #1e293b;
                }

                /* --- BOOTH GRID & CARD STYLES --- */
                .booth-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
                    gap: 24px;
                }

                .booth-card {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 28px 20px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                    border: 1px solid #f1f5f9;
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                    cursor: pointer; /* Makes it obvious it is clickable */
                }

                .booth-card:hover {
                    transform: translateY(-6px);
                    box-shadow: 0 15px 30px -5px rgba(0, 0, 0, 0.1);
                    border-color: #e2e8f0;
                }

                .card-top-accent {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 5px;
                    background: linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%);
                }

                .booth-label {
                    font-size: 13px;
                    font-weight: 700;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    margin: 0 0 12px 0;
                }

                .booth-number-circle {
                    width: 85px;
                    height: 85px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
                    color: #1d4ed8;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 32px;
                    font-weight: 900;
                    border: 4px solid #ffffff;
                    box-shadow: 0 8px 16px rgba(37, 99, 235, 0.15);
                    margin-bottom: 24px;
                }

                .voter-stats-container {
                    width: 100%;
                    background: #f8fafc;
                    border-radius: 12px;
                    padding: 16px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    border: 1px solid #f1f5f9;
                }

                .voter-icon-wrapper {
                    width: 44px;
                    height: 44px;
                    background: #ffffff;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #3b82f6;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                    flex-shrink: 0;
                }

                .voter-info {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                }

                .voter-title {
                    font-size: 12px;
                    color: #64748b;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .voter-count {
                    font-size: 20px;
                    font-weight: 800;
                    color: #0f172a;
                    line-height: 1.2;
                }

                /* --- EMPTY STATE --- */
                .no-data-message {
                    grid-column: 1 / -1;
                    text-align: center;
                    padding: 60px 20px;
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                    border: 1px dashed #cbd5e1;
                }

                .no-data-icon {
                    font-size: 54px;
                    margin-bottom: 16px;
                }

                .no-data-text {
                    font-size: 16px;
                    font-weight: 600;
                    color: #64748b;
                }

                /* --- MOBILE RESPONSIVENESS --- */
                @media (max-width: 640px) {
                    .booth-grid {
                        grid-template-columns: 1fr;
                        gap: 16px;
                    }

                    .booth-card {
                        padding: 24px 20px;
                    }

                    .booth-number-circle {
                        width: 75px;
                        height: 75px;
                        font-size: 28px;
                    }

                    .voter-count {
                        font-size: 18px;
                    }
                }
            `}</style>
        </div>
    );
};

export default YourBooth;